import { useState } from 'react';
import { Modal, ModalBody, Button, Stack, Text } from '@7shifts/sous-chef-base';
import type {
    AttestationRequiredResponse,
    AttestationContent,
    PunchResponse
} from '@src/api/punchTypes';
import { useLocale, useTranslation } from '@src/i18n';
import { AttestationIcon } from './icons/AttestationIcon';
import './AttestationModal.css';

function resolveContent(
    contents: AttestationContent[],
    locale: string
): AttestationContent | undefined {
    const primary = locale.split('-')[0];
    return (
        contents.find(c => c.language === locale) ??
        contents.find(c => c.language.startsWith(primary)) ??
        contents[0]
    );
}

interface AttestationModalProps {
    response: AttestationRequiredResponse;
    onExecute: (execute: () => Promise<PunchResponse>) => void;
    onDismiss: () => void;
}

export function AttestationModal({
    response,
    onExecute,
    onDismiss
}: AttestationModalProps) {
    const locale = useLocale();
    const { t } = useTranslation();
    const content = resolveContent(response.attestation.contents, locale);
    const [submitted, setSubmitted] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    if (!content) {
        return null;
    }

    function handleConfirm() {
        if (submitted || selectedId === null) return;
        setSubmitted(true);
        onExecute(() => response.sendAttestationResponse(selectedId));
    }

    return (
        <Modal testId="attestation-modal">
            <ModalBody>
                <Stack space={12} alignItems="center">
                    <div className="attestation-modal__icon" aria-hidden="true">
                        <AttestationIcon />
                    </div>
                    <Text as="h3" alignment="center">
                        {content.question}
                    </Text>
                    <Stack space={8}>
                        {content.responses.map(option => (
                            <label
                                key={option.id}
                                className={`attestation-modal__option${
                                    selectedId === option.id
                                        ? ' attestation-modal__option--selected'
                                        : ''
                                }`}
                                data-testid={`attestation-response-${option.id}`}
                            >
                                <input
                                    type="radio"
                                    name="attestation"
                                    value={option.id}
                                    checked={selectedId === option.id}
                                    onChange={() => setSelectedId(option.id)}
                                    disabled={submitted}
                                    className="attestation-modal__radio"
                                />
                                <span className="attestation-modal__option-text">
                                    {option.text}
                                </span>
                            </label>
                        ))}
                    </Stack>
                </Stack>
                <Stack space={8}>
                    <Button
                        type="button"
                        theme="primary"
                        size="full-width"
                        disabled={selectedId === null || submitted}
                        loading={submitted}
                        onClick={handleConfirm}
                        testId="attestation-confirm"
                    >
                        {t('attestation.confirm')}
                    </Button>
                    <Button
                        type="button"
                        theme="hollow"
                        size="full-width"
                        disabled={submitted}
                        onClick={onDismiss}
                        testId="attestation-cancel"
                    >
                        {t('common.cancel')}
                    </Button>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
